import React from "react";
import { Col, Row } from "antd";
import _ from "lodash";

import formFieldComponents from "../components/form/utils/formFieldComponents";
import CustomFieldEngine from "../../../customField/customFieldEngine";
import SectionPart from "../components/SectionPart";
import * as validationRules from "./index";
import { evaluateFieldCondition } from "./formConditions";

export const getFieldName = (sectionName, field) => {
  if (field.subSectionName) {
    return [sectionName, field.subSectionName, field.name];
  }
  return [sectionName, field.name];
};

export const getValidationRules = (field) => {
  const { rules: ruleNames = [], rulesParams = {} } = field;
  if (!ruleNames.length) {
    return [];
  }
  const rulesArr = ruleNames.map((ruleName) => {
    const rule = validationRules[ruleName];
    if (!rule) {
      return null;
    }
    if (typeof rule === "function" && rulesParams[ruleName]) {
      return rule(rulesParams[ruleName] || {});
    }
    return rule;
  });

  return _.compact(_.flatten(rulesArr));
};

// A section skips the SectionPart divider/anchor when one of its fields renders its OWN section chrome
// (divider + anchor id) — e.g. the native `eligibilityQuestionnaire` field via DynamicQuestionnaire — so
// the wrapper doesn't add an empty divider or a duplicate `enrollment-form-*` id. Deriving this from the
// field type (the component declares `rendersOwnSection`) keeps the decision where the knowledge lives
// and removes the need for a per-section flag + `sectionHeader: ""` sentinel. `layout: "bare"` is an
// explicit opt-out for a deliberately wrapper-less section of otherwise-normal fields.
const sectionRendersOwnChrome = (section) =>
  section?.layout === "bare" ||
  (section?.rows || []).some((row) =>
    (row.fields || []).some((field) => formFieldComponents[field.type]?.rendersOwnSection)
  );

const renderReactFromJson = (form, json) => {
  return json.formSections.map((section) => {
    if (section.conditions?.visibility && !evaluateFieldCondition(section.conditions.visibility, form)) {
      return null;
    }
    const rows = renderReactRowsJson({ section, rows: section.rows, form });
    if (sectionRendersOwnChrome(section)) {
      return <React.Fragment key={section.sectionName}>{rows}</React.Fragment>;
    }
    return (
      <SectionPart
        key={section.sectionName}
        sectionId={section.sectionName}
        headerTranslationKey={section.sectionHeader}
      >
        {rows}
      </SectionPart>
    );
  });
};

export const renderReactRowsJson = ({ section, rows, form }) => {
  return rows.map((row, rowIndex) => (
    <Row key={`${section?.sectionName}_row_${rowIndex}`} gutter={[16, 16]}>
      {row.fields.map((field) => {
        const FieldComponent = formFieldComponents[field.type] || CustomFieldEngine;
        const span = field.colSize || 8;

        let isVisible = true;
        let isDisabled = field.fieldProps?.disabled || false;
        let isRequired = field.required || false;

        if (field.conditions) {
          const { visibility, disabled, required } = field.conditions;
          if (visibility) {
            isVisible = evaluateFieldCondition(visibility, form);
          }
          if (!isVisible) {
            return null;
          }
          if (required) {
            isRequired = evaluateFieldCondition(required, form);
          }
          if (disabled) {
            isDisabled = evaluateFieldCondition(disabled, form);
          }
        }

        return (
          <Col span={span} key={`${section.sectionName}_${field.name}_${field.label}`}>
            <FieldComponent
              type={field.type}
              name={getFieldName(section.sectionName, field)}
              label={field.label}
              required={isRequired}
              rules={getValidationRules(field)}
              placeholder={field.placeholder}
              fieldProps={{ ...field.fieldProps, ...(isDisabled && { disabled: true }) }}
              formItemProps={field.formItemProps}
              form={form}
              {...field.dedicatedFieldProps}
            />
          </Col>
        );
      })}
    </Row>
  ));
};

export default renderReactFromJson;
